import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { MailCheckIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { z } from "zod";

import { AuthShell } from "@/app/auth/AuthShell";
import { FormRootError, TextField } from "@/components/app/form-field";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { api, toApiError } from "@/lib/api";

const schema = z.object({
  email: z.string().trim().email("auth.emailInvalid"),
});

type FormValues = z.infer<typeof schema>;

/**
 * `POST /auth/password/reset/` always answers 202 — it must not reveal whether
 * an address exists, on this tenant or any other (contract §5.5, §6.6).
 */
export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { email: "" } });

  const onSubmit = async (values: FormValues) => {
    try {
      await api.post("auth/password/reset/", values);
      setSent(true);
    } catch (error) {
      const apiError = toApiError(error);
      // A throttle is the one failure worth showing; anything else would leak.
      form.setError("root", { message: apiError.detail || t("error.unexpected") });
    }
  };

  return (
    <AuthShell
      title={t("auth.forgotTitle")}
      description={sent ? undefined : t("auth.forgotSubtitle")}
      footer={
        <Link
          to="/login"
          className="text-muted-foreground hover:text-foreground underline underline-offset-4"
        >
          {t("auth.backToLogin")}
        </Link>
      }
    >
      {sent ? (
        <div className="flex flex-col items-center gap-3 py-4 text-center">
          <MailCheckIcon className="text-success size-10" aria-hidden="true" />
          <p className="text-sm">{t("auth.forgotSent")}</p>
        </div>
      ) : (
        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
            <FormRootError message={form.formState.errors.root?.message} />
            <TextField
              control={form.control}
              name="email"
              type="email"
              autoComplete="email"
              label={t("common.email")}
              required
            />
            <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? t("action.saving") : t("auth.forgotSubmit")}
            </Button>
          </form>
        </Form>
      )}
    </AuthShell>
  );
}
